/**
 * <tf-input> — Premium input con label, error, icon, type variants.
 *
 * Sostituisce il pattern duplicato N volte di `<input>` con label + helper text.
 *
 * Uso HTML:
 *   <tf-input label="Email" type="email" required></tf-input>
 *   <tf-input label="Password" type="password" placeholder="••••••"></tf-input>
 *   <tf-input label="P.IVA" type="text" pattern="[0-9]{11}" hint="11 cifre"></tf-input>
 *
 * Attributes:
 *   - label: string (visualizzato sopra)
 *   - type: 'text' | 'email' | 'password' | 'tel' | 'number' | 'url'
 *   - placeholder: string
 *   - value: string (initial value)
 *   - hint: string (testo helper sotto, italics)
 *   - error: string (messaggio errore, override hint, rosso)
 *   - required: '' (presence)
 *   - disabled: '' (presence)
 *   - icon: 'email' | 'lock' | 'user' | 'phone' | 'building' (preset SVG)
 *   - size: 'sm' | 'md' | 'lg'
 *
 * Methods:
 *   - element.value getter/setter
 *   - element.focus()
 *   - element.validate(): boolean
 *
 * Events:
 *   - tf-input-change (debounced): { value }
 *
 * @since v9.16 (Sprint 9)
 */

const ICONS: Record<string, string> = {
  email: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 1 0-2.636 6.364M16.5 12V8.25"/></svg>',
  lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>',
  user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/></svg>',
  phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"/></svg>',
  building: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"/></svg>',
};

const SIZES = {
  sm: 'h-9 text-sm px-3',
  md: 'h-11 text-sm px-3.5',
  lg: 'h-12 text-base px-4',
};

export class TfInputElement extends HTMLElement {
  private _input?: HTMLInputElement;
  private _debounceTimer: number | null = null;

  static get observedAttributes(): string[] {
    return ['label', 'type', 'placeholder', 'value', 'hint', 'error', 'required', 'disabled', 'icon', 'size'];
  }

  connectedCallback(): void {
    this._render();
  }

  disconnectedCallback(): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  attributeChangedCallback(): void {
    if (this._input) this._render();
  }

  // ============================================================
  // Public API
  // ============================================================

  get value(): string {
    return this._input?.value || '';
  }

  set value(v: string) {
    if (this._input) this._input.value = v;
  }

  override focus(): void {
    this._input?.focus();
  }

  validate(): boolean {
    if (!this._input) return false;
    return this._input.checkValidity();
  }

  // ============================================================
  // Internal
  // ============================================================

  private _render(): void {
    const label = this.getAttribute('label') || '';
    const type = this.getAttribute('type') || 'text';
    const placeholder = this.getAttribute('placeholder') || '';
    const value = this.getAttribute('value') || '';
    const hint = this.getAttribute('hint') || '';
    const error = this.getAttribute('error') || '';
    const required = this.hasAttribute('required');
    const disabled = this.hasAttribute('disabled');
    const icon = this.getAttribute('icon');
    const size = (this.getAttribute('size') as keyof typeof SIZES) || 'md';
    const sizeCls = SIZES[size] || SIZES.md;
    const iconSvg = icon && ICONS[icon] ? ICONS[icon] : '';
    const padLeft = iconSvg ? 'pl-10' : '';

    this.innerHTML = `
      ${label ? `<label class="mb-1.5 block text-sm font-medium text-secondary">${this._esc(label)}${required ? ' <span class="text-red-500">*</span>' : ''}</label>` : ''}
      <div class="relative">
        ${iconSvg ? `<span class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400">${iconSvg}</span>` : ''}
        <input
          type="${this._esc(type)}"
          class="tf-input-field w-full rounded-xl border outline-none transition-all
                 ${error ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100' : 'border-stroke-1 focus:border-amber-500 focus:ring-2 focus:ring-amber-100'}
                 ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-60' : 'bg-white'}
                 ${sizeCls} ${padLeft}"
          ${placeholder ? `placeholder="${this._esc(placeholder)}"` : ''}
          ${value ? `value="${this._esc(value)}"` : ''}
          ${required ? 'required' : ''}
          ${disabled ? 'disabled' : ''}
        />
      </div>
      <div class="tf-input-helper mt-1.5 text-xs ${error ? 'text-red-600' : 'text-slate-500'}">${this._esc(error || hint)}</div>
    `;

    this._input = this.querySelector<HTMLInputElement>('.tf-input-field')!;

    this._input.addEventListener('input', () => {
      const v = this._input!.value;
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      this._debounceTimer = window.setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent('tf-input-change', { bubbles: true, detail: { value: v } })
        );
      }, 150);
    });
  }

  private _esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-input')) {
  customElements.define('tf-input', TfInputElement);
}
